import {
  CARD,
  IMAGE_MAP,
  LayoutColumnsDefinition,
  LayoutListDefinition,
  LAYOUT_COLUMNS,
  LAYOUT_LIST,
  POPUP,
  PopupDefinition,
  TEXT,
  AUDIO_POPUP,
  AudioPopupDefinition
} from '../items';
import { Translator } from '../Translator';
import { translate } from './translate';
import { useEditorStore } from '~/shell/Store';
import { isMobile } from 'react-device-detect';
import {
  INTERACTIVE4_BTN_SIZE as BTN_SIZE,
  resolveInteractive4Overlaps
} from './interactive4Overlap';

export const Interactive4: Translator = sequence => {
  const { editorMode } = useEditorStore.getState();

  const columns = {
    type: LAYOUT_COLUMNS,
    id: `${sequence.id}-columns`,
    items: [],
    values: {}
  } as LayoutColumnsDefinition;

  let maxWidth = 1200;
  let maxHeight = 800;

  let originMaxWidth = 1200;
  let originMaxHeight = 800;

  const getCoordinatesInPercentage = (
    left: number,
    top: number,
    maxWidth: number,
    maxHeight: number
  ) => {
    return {
      left: (left * 100) / maxWidth,
      top: (top * 100) / maxHeight
    };
  };

  /**
   * https://linear.app/easyllama/issue/ENG-9451/go1-support-issue-content-icons-overlapping
   *
   * The Problem:
   * - Sometimes the content team changes the image size without updating the popup coordinates.
   * - Or they change the popup coordinates without checking how they are displayed.
   * - The overlapping of buttons has a significant negative impact on the user experience.
   *
   * To address this, I implemented an algorithm that automatically shifts buttons when an overlap is detected.
   *
   * Resolve overlapping popup buttons by nudging them minimally while
   * preserving the original spatial pattern.
   *
   * 1. Sort buttons by proximity to the top-left corner (anchor first).
   * 2. For each non-anchor button, find the worst overlap and nudge it
   *    on the axis needing the least displacement, in its natural
   *    direction.  The move is validated against the target conflict
   *    before it is accepted; fallback strategies (opposite direction,
   *    other axis, diagonal) are tried in order.
   * 3. Every button is processed per pass; passes repeat until stable.
   *
   * Runs whenever there are 2+ hotspots so editor (AudioPoint) and learner (POPUP / AUDIO_POPUP) share the same nudged layout.
   */
  const root = translate(sequence, item => {
    switch (item.__typename) {
      case 'ImageMap': {
        if (
          item.pictures &&
          item.pictures.length &&
          item.pictures?.[0]?.mediaUrl
        ) {
          maxWidth = item.pictures?.[0]?.width as number;
          maxHeight = item.pictures?.[0]?.height as number;

          originMaxWidth = maxWidth;
          originMaxHeight = maxHeight;
        }

        if (!editorMode) {
          maxWidth = 400;
          maxHeight = 420;
        }

        const popupItems = item.items || [];

        const positions = popupItems.map(i => {
          const parts = (i.coordinates || '').split(',');
          const coord = getCoordinatesInPercentage(
            parseInt(parts[0]),
            parseInt(parts[1]),
            originMaxWidth,
            originMaxHeight
          );
          // Same base % for editor and learner so ImageMap / AudioPoint matches POPUP layout.
          let left = coord.left;
          let top = coord.top;
          if (isMobile) {
            if (left > 80) left = 80;
            if (top > 80) top = 80;
          }
          return { left, top };
        });

        if (positions.length > 1) {
          resolveInteractive4Overlaps(positions, isMobile);
        }

        columns.items.push({
          id: 'LAYOUT_LIST',
          type: LAYOUT_LIST,
          values: { align: 'center' },
          items: [
            {
              type: CARD,
              id: '1',
              values: {
                clearStyles: 'lg',
                height: '420px',
                width: '400px',
                padding: '0px'
              },
              items: [
                {
                  type: IMAGE_MAP,
                  id: item.id,
                  values: {
                    imageUrl: item.pictures?.[0]?.mediaUrl,
                    w: '400px',
                    h: '420px',
                    /** Anchored POPUP/AUDIO_POPUP: no extra +100px drift; keep card inside photo on ultrawide */
                    clampPopupToImageMap: true,
                    popupFixedOffsetLeftPx: 0,
                    popupFixedOffsetTopPx: 20
                  },
                  items: popupItems.map((i, index) => {
                    const _left = positions[index].left;
                    const _top = positions[index].top;

                    if (i.audios && i.audios.length > 0) {
                      return {
                        type: AUDIO_POPUP,
                        id: `${(i as Record<string, unknown>).id ?? item.id}-${index}`,
                        items: [],
                        values: {
                          title:
                            item.variant === 'number'
                              ? index + 1
                              : i.title &&
                                  typeof i.title === 'string' &&
                                  /^\d+$/.test(i.title.trim())
                                ? i.title.trim()
                                : null,
                          left: _left,
                          top: _top,
                          name:
                            i.title &&
                            typeof i.title === 'string' &&
                            /^\d+$/.test(i.title.trim())
                              ? ''
                              : i.title,
                          body: i.body,
                          mediaUrl: i.audios?.[0]?.mediaUrl,
                          audioId: i.audios?.[0]?.id
                            ? Number(i.audios[0].id)
                            : undefined,
                          subtitlesUrl: i.audios?.[0]?.subtitlesUrl,
                          blocking: true,
                          replay: true,
                          isPopupCentered: isMobile,
                          openPopupUnderButton: !isMobile,
                          popupFixedOffsetLeftPx: 0,
                          popupFixedOffsetTopPx: 20,
                          btnSize: BTN_SIZE
                        }
                      } as AudioPopupDefinition;
                    } else {
                      return {
                        type: POPUP,
                        id: `${(i as Record<string, unknown>).id ?? item.id}-${index}`,
                        values: {
                          btnTitle:
                            item.variant === 'number'
                              ? index + 1
                              : i.title &&
                                  typeof i.title === 'string' &&
                                  /^\d+$/.test(i.title.trim())
                                ? i.title.trim()
                                : '+',
                          left: _left,
                          top: _top,
                          title: i.title,
                          subtitle: i.subtitle,
                          body: i.body,
                          blocking: true,
                          btnSize: BTN_SIZE,
                          isPopupCentered: isMobile,
                          openPopupUnderButton: !isMobile,
                          popupFixedOffsetLeftPx: 0,
                          popupFixedOffsetTopPx: 20,
                          metric: '%'
                        }
                      } as PopupDefinition;
                    }
                  })
                }
              ]
            }
          ]
        });
        break;
      }
      case 'Text':
      case 'Question':
        columns.items.push({
          id: 'LAYOUT_LIST_RIGHT',
          type: LAYOUT_LIST,
          values: { align: 'left', padded: true },
          items: [
            {
              type: TEXT,
              id: `${item.id}_subtitle`,
              items: [],
              values: {
                value: item.subtitle,
                variant: 'sub_heading',
                icon: item.icon
              }
            },
            {
              type: TEXT,
              id: `${item.id}_title`,
              items: [],
              values: {
                value: item.title,
                variant: 'heading2'
              }
            },
            {
              type: TEXT,
              id: `${item.id}_body`,
              items: [],
              values: {
                value: item.body
              }
            }
          ]
        } as LayoutListDefinition);
        break;
    }
    return null;
  });

  root.items.push(columns);
  return root;
};
